import classNames from 'classnames';
import {
  type AllHTMLAttributes,
  type ElementType,
  forwardRef,
  type ReactNode,
} from 'react';

import styles from './Text.module.css';

type TextSize =
  | 'huge'
  | 'x-lg'
  | 'lg'
  | 'x-bg'
  | 'bg'
  | 'md'
  | 'sm'
  | 'x-sm'
  | 'xx-sm'
  | 'inherit';

type TextWeight = 'regular' | 'semibold' | 'bold' | 'inherit';

type TextTone = 'normal' | 'muted' | 'secondary' | 'inherit';

type TextLineClamp = 1 | 2 | 3;

export interface TextProps extends Omit<AllHTMLAttributes<HTMLElement>, 'as'> {
  as?: ElementType;
  fontSize?: TextSize;
  weight?: TextWeight;
  tone?: TextTone;
  lineClamp?: TextLineClamp;
  includePadding?: boolean;
  children?: ReactNode;
}

export const Text = forwardRef<HTMLElement, TextProps>(function Text(
  {
    as,
    fontSize = 'md',
    weight = 'regular',
    tone = 'normal',
    lineClamp,
    includePadding = false,
    children,
    className,
    ...rest
  },
  ref
) {
  const Component = (as ?? 'p') as ElementType;

  return (
    <Component
      ref={ref}
      className={classNames(
        styles.base,
        styles[fontSize],
        styles[`weight-${weight}`],
        tone !== 'normal' && styles[`tone-${tone}`],
        lineClamp && styles.clamp,
        lineClamp && styles[`clamp-${lineClamp}`],
        includePadding && styles.withPadding,
        className
      )}
      {...rest}
    >
      {children}
    </Component>
  );
});
